import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { type WritableDraft } from 'immer';
import { map, type Observable, of, switchMap, take } from 'rxjs';

import { type CanvasElementFrom, type CanvasElementId } from '@blaze-runtime/engine-domain';
import {
  type PropertiesConfigInputContainerItemUnifiedBulkActionModel,
  type PropertiesConfigInputContainerItemUnifiedRowActionModel,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { type BlazeSchema, findColumn, getColumnKey } from '@blaze-shared/schema-domain';
import {
  findFirstNodeInWorkflowOfType,
  isGetNode,
  type Workflow,
} from '@blaze-shared/workflow-domain';

import { updateElementProps } from '../../../+state/elements';
import { type EngineState } from '../../../+state/engine';
import {
  AbstractAiElementHandler,
  addEventWorkflowWithConnection,
  addSourceWorkflow,
  type AiWorkflowFinalizationEntry,
  getElement,
  RecordActionsHandlerService,
  resolveBulkActions,
  type ResolvedRecordActions,
  resolveFullWidth,
  resolveRecordActions,
  resolveRecordClick,
  upsertElement,
} from '../../../ai';
import { ContainerFieldFiltersApplierService } from '../../../ai/page/container-field-filters-applier.service';

import { toEngineSortRequest } from './list.appearance';
import { type ListContext, type ListResolvedContext, type SortByEntry } from './list.definition';
import { buildListProps } from './list.prop-builders';

// example: {{ field "columnName" }}
const TEMPLATE_BLOCK_REGEX = /{{([\s\S]*?)}}/g;
// example: field "columnName" or (field "columnName")
const TEMPLATE_FIELD_REGEX = /\(\s*field\s+"([^"]*)"\s*\)|field\s+"([^"]*)"/g;

type ResolvedItemActions =
  ResolvedRecordActions<PropertiesConfigInputContainerItemUnifiedRowActionModel>;
type ResolvedBulkActions =
  ResolvedRecordActions<PropertiesConfigInputContainerItemUnifiedBulkActionModel>;

export class ListElementHandler extends AbstractAiElementHandler<'list'> {
  private readonly containerFieldFiltersApplier = inject(ContainerFieldFiltersApplierService);
  private readonly recordActionsHandlerService = inject(RecordActionsHandlerService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);

  readonly type = 'list';

  prepare(context: ListContext): Observable<ListResolvedContext> {
    this.resolveIcons(context.element, context.element.itemActions, context.element.bulkActions);

    const workflowContext = this.resolveWorkflowContext(context);
    const customTemplateCode = this.resolveCustomTemplateCode(context);
    return of({ ...context, customTemplateCode, ...(workflowContext ? { workflowContext } : {}) });
  }

  process(context: ListResolvedContext, draft: WritableDraft<EngineState>): void {
    const { props: itemActionProps, workflows: itemActionWorkflows }: ResolvedItemActions =
      resolveRecordActions(
        context.element.id,
        context.element.itemActions,
        context.componentWorkflows,
        'ItemActions',
      );
    const { props: bulkActionProps, workflows: bulkActionWorkflows }: ResolvedBulkActions =
      resolveBulkActions(
        context.element.id,
        context.element.bulkActions,
        context.componentWorkflows,
      );
    const resolvedItemClick = resolveRecordClick(
      context.element.id,
      context.element.itemClick,
      context.componentWorkflows,
      'ItemClick',
    );

    const props = buildListProps(context, draft, {
      itemActions: itemActionProps,
      bulkActions: bulkActionProps,
      itemClick: resolvedItemClick.props,
    });
    const result = upsertElement(draft, context, props);
    const element = getElement(draft, result.elementId, 'list');
    const workflowsToFinalize: AiWorkflowFinalizationEntry[] = [];

    if (context.workflowContext) {
      this.applySortToWorkflow(context.element.sortBy, context.workflowContext.workflow);
      addSourceWorkflow(draft, context.workflowContext.workflow);
    }

    const customActionWorkflows = [...itemActionWorkflows, ...bulkActionWorkflows];
    if (resolvedItemClick.workflow) {
      customActionWorkflows.push(resolvedItemClick.workflow);
    }

    customActionWorkflows.forEach((workflow) => {
      const { hasUpdates } = addEventWorkflowWithConnection(draft, context, workflow);
      workflowsToFinalize.push({ workflow, forceUpdate: hasUpdates });
    });

    context.workflowsToFinalize = workflowsToFinalize;
    element.width = resolveFullWidth(context.element.width);
  }

  finalize(element: CanvasElementFrom<'list'>, context: ListResolvedContext): Observable<void> {
    const sourceCreation$ = context.workflowContext
      ? this.finalizeSourceWorkflow(element, context.workflowContext)
      : of(undefined);

    const updateWorkflows$ = this.aiWorkflowGenerationService.handleAiGeneratedWorkflows(
      context.workflowsToFinalize ?? [],
    );

    return sourceCreation$.pipe(
      switchMap(() => updateWorkflows$),
      switchMap(() =>
        this.recordActionsHandlerService.finalizeGeneratedPages(
          element.id,
          context.element.itemActions,
          context.element.itemClick,
        ),
      ),
      switchMap(() =>
        this.recordActionsHandlerService.finalizeDeleteWorkflow(
          element.id,
          context.element.itemActions,
        ),
      ),
      switchMap(() =>
        this.recordActionsHandlerService.finalizeBulkActionWorkflows(
          element.id,
          context.element.bulkActions,
        ),
      ),
      switchMap(() =>
        this.recordActionsHandlerService.finalizeAddNew(element.id, !!context.element.addNewItem),
      ),
      switchMap(() => this.updateElementCustomTemplate(element, context)),
      switchMap(() => this.applyFieldFilters(element.id, context)),
    );
  }

  private applySortToWorkflow(sortBy: SortByEntry[] | undefined, workflow: Workflow): void {
    if (!sortBy?.length) {
      return;
    }

    const sourceNode = findFirstNodeInWorkflowOfType(workflow, isGetNode);
    if (sourceNode) {
      sourceNode.sorts = sortBy.map(toEngineSortRequest);
    }
  }

  private applyFieldFilters(
    elementId: CanvasElementId,
    context: ListResolvedContext,
  ): Observable<void> {
    const datasetId = findFirstNodeInWorkflowOfType(
      context.workflowContext?.workflow,
      isGetNode,
    )?.datasetId;

    return this.containerFieldFiltersApplier.apply({
      elementId,
      elementType: 'list',
      fieldFilters: context.element.fieldFilters,
      datasetId,
    });
  }

  private updateElementCustomTemplate(
    element: CanvasElementFrom<'list'>,
    context: ListResolvedContext,
  ): Observable<void> {
    const { customTemplateCode } = context;
    const workflowId = context.workflowContext?.workflow.id;

    if (!customTemplateCode) {
      return of(undefined);
    }

    const updateElementCustomTemplateAsync = (code: string): void => {
      this.store.dispatch(
        updateElementProps({
          elementId: element.id,
          path: 'content.list.customTemplate.code',
          value: code,
        }),
      );
    };

    if (!workflowId) {
      updateElementCustomTemplateAsync(customTemplateCode);
      return of(undefined);
    }

    return this.schemaService.observeWorkflowSchema(workflowId).pipe(
      take(1),
      map((schema) => {
        const parsed = this.prepareCustomTemplate(customTemplateCode, schema);
        updateElementCustomTemplateAsync(parsed);
        return undefined;
      }),
    );
  }

  private prepareCustomTemplate(code: string, schema: BlazeSchema | undefined): string {
    return code.replace(
      TEMPLATE_BLOCK_REGEX,
      (_block, blockContent: string) =>
        `{{${blockContent.replace(
          TEMPLATE_FIELD_REGEX,
          (_fieldMatch, parenFieldName: string | undefined, bareFieldName: string | undefined) => {
            const fieldName = parenFieldName ?? bareFieldName;
            const column = findColumn(schema?.columns, { name: fieldName });
            return column ? `[$variable(${getColumnKey(column)})]` : `field "${fieldName}"`;
          },
        )}}}`,
    );
  }
}
